import {
  type ImageRect,
  type PercentagePoint,
  type PercentageRect,
  type PixelPoint,
  type PixelRect,
} from '~/components/EditMode/components/BoundingBoxLayerKonva/types';

/**
 * Service class for converting between different coordinate systems
 * Handles conversion between percentage-based and pixel-based coordinates
 */
export class PositionedImage {
  public containerWidth: number;
  public containerHeight: number;
  public imageWidth: number;
  public imageHeight: number;
  public imageX: number;
  public imageY: number;
  public initialImageX: number;
  public initialImageY: number;
  public originalImageWidth: number;
  public originalImageHeight: number;
  public setImagePosition: (offset: { x: number; y: number }) => void;
  public imageScale: number;
  public setImageScale: (scale: number) => void;

  constructor({
    containerWidth,
    containerHeight,
    imageWidth,
    imageHeight,
    imageX,
    imageY,
    initialImageX,
    initialImageY,
    originalImageWidth,
    originalImageHeight,
    setImagePosition,
    imageScale,
    setImageScale,
  }: {
    containerWidth: number;
    containerHeight: number;
    imageWidth: number;
    imageHeight: number;
    imageX: number;
    imageY: number;
    initialImageX: number;
    initialImageY: number;
    originalImageWidth: number;
    originalImageHeight: number;
    setImagePosition: (offset: { x: number; y: number }) => void;
    imageScale: number;
    setImageScale: (scale: number) => void;
  }) {
    this.containerWidth = containerWidth;
    this.containerHeight = containerHeight;
    this.imageWidth = imageWidth;
    this.imageHeight = imageHeight;
    this.imageX = imageX;
    this.imageY = imageY;
    this.initialImageX = initialImageX;
    this.initialImageY = initialImageY;
    this.originalImageWidth = originalImageWidth;
    this.originalImageHeight = originalImageHeight;
    this.setImagePosition = setImagePosition;
    this.imageScale = imageScale;
    this.setImageScale = setImageScale;
  }

  get image() {
    return {
      pctStartX: this.imageX / this.containerWidth,
      pctStartY: this.imageY / this.containerHeight,
      pctEndX: (this.imageX + this.imageWidth) / this.containerWidth,
      pctEndY: (this.imageY + this.imageHeight) / this.containerHeight,
      pctWidth: this.imageWidth / this.containerWidth,
      pctHeight: this.imageHeight / this.containerHeight,
      pxStartX: this.imageX,
      pxStartY: this.imageY,
      pxEndX: this.imageX + this.imageWidth,
      pxEndY: this.imageY + this.imageHeight,
      pxWidth: this.imageWidth,
      pxHeight: this.imageHeight,
      aspectRatio: this.imageWidth / this.imageHeight,
      scale: this.originalImageWidth / this.imageWidth,
    };
  }

  get container() {
    return {
      pctStartY: 0,
      pctStartX: 0,
      pctEndX: 1,
      pctEndY: 1,
      pctWidth: 1,
      pctHeight: 1,
      pxStartX: 0,
      pxStartY: 0,
      pxEndX: this.containerWidth,
      pxEndY: this.containerHeight,
      pxWidth: this.containerWidth,
      pxHeight: this.containerHeight,
      aspectRatio: this.containerWidth / this.containerHeight,
    };
  }

  toImageRect(rect: PercentageRect): ImageRect {
    return {
      x: Math.round((rect.pctStartX * this.container.pxWidth - this.image.pxStartX) * this.image.scale),
      y: Math.round((rect.pctStartY * this.container.pxHeight - this.image.pxStartY) * this.image.scale),
      width: Math.round((rect.pctEndX - rect.pctStartX) * this.container.pxWidth * this.image.scale),
      height: Math.round((rect.pctEndY - rect.pctStartY) * this.container.pxHeight * this.image.scale),
    };
  }

  originalDimensionsToPercentageRect(boundingBox: PercentageRect, newWidth: number, newHeight: number): PercentageRect {
    // Clamp dimensions to valid range (1 to image dimensions)
    const clampedWidth = Math.max(1, Math.min(newWidth / this.image.scale, this.image.pxWidth));
    const clampedHeight = Math.max(1, Math.min(newHeight / this.image.scale, this.image.pxHeight));

    // keep the left and top the same
    const pctStartX = boundingBox.pctStartX;
    const pctStartY = boundingBox.pctStartY;
    const pctEndX = pctStartX + clampedWidth / this.container.pxWidth;
    const pctEndY = pctStartY + clampedHeight / this.container.pxHeight;

    return {
      pctStartX,
      pctStartY,
      pctEndX,
      pctEndY,
    };
  }

  toFramedImageRect(rect: PercentageRect) {
    return {
      frame: {
        width: Math.round((rect.pctEndX - rect.pctStartX) * this.container.pxWidth * this.image.scale),
        height: Math.round((rect.pctEndY - rect.pctStartY) * this.container.pxHeight * this.image.scale),
      },
      position: {
        x: Math.round((this.image.pctStartX - rect.pctStartX) * this.container.pxWidth * this.image.scale),
        y: Math.round((this.image.pctStartY - rect.pctStartY) * this.container.pxHeight * this.image.scale),
      },
    };
  }

  /**
   * Convert a PixelRect (pixel coordinates) to PercentageRect
   */
  toPercentageRect(rect: PixelRect): PercentageRect {
    return {
      pctStartX: rect.startX / this.container.pxWidth,
      pctStartY: rect.startY / this.container.pxHeight,
      pctEndX: rect.endX / this.container.pxWidth,
      pctEndY: rect.endY / this.container.pxHeight,
    };
  }

  /**
   * Convert a PercentageRect to PixelRect (pixel coordinates)
   */
  toPixelRect(rect: PercentageRect): PixelRect {
    return {
      startX: rect.pctStartX * this.container.pxWidth,
      startY: rect.pctStartY * this.container.pxHeight,
      endX: rect.pctEndX * this.container.pxWidth,
      endY: rect.pctEndY * this.container.pxHeight,
    };
  }

  /**
   * Convert a Point (pixel coordinates) to PercentagePoint
   */
  toPercentagePoint(point: PixelPoint): PercentagePoint {
    return {
      pctX: point.x / this.container.pxWidth,
      pctY: point.y / this.container.pxHeight,
    };
  }

  /**
   * Convert a PercentagePoint to Point (pixel coordinates)
   */
  toPixelPoint(point: PercentagePoint): PixelPoint {
    return {
      x: point.pctX * this.container.pxWidth,
      y: point.pctY * this.container.pxHeight,
    };
  }

  /**
   * Clamp a point to container bounds
   */
  clampPoint(point: PixelPoint): PixelPoint {
    return {
      x: Math.max(0, Math.min(point.x, this.container.pxWidth)),
      y: Math.max(0, Math.min(point.y, this.container.pxHeight)),
    };
  }

  clampPercentageRect(rect: PercentageRect): PercentageRect {
    // 12 px padding for rounded corners
    const paddingPctX = 12 / this.container.pxWidth;
    const paddingPctY = 12 / this.container.pxHeight;

    return {
      pctStartX: Math.max(0 + paddingPctX, Math.min(1 - paddingPctX, rect.pctStartX)),
      pctStartY: Math.max(0 + paddingPctY, Math.min(1 - paddingPctY, rect.pctStartY)),
      pctEndX: Math.max(0 + paddingPctX, Math.min(1 - paddingPctX, rect.pctEndX)),
      pctEndY: Math.max(0 + paddingPctY, Math.min(1 - paddingPctY, rect.pctEndY)),
    };
  }

  clampPointToImageBounds(point: PixelPoint): PixelPoint {
    return {
      x: Math.max(this.image.pxStartX, Math.min(point.x, this.image.pxEndX)),
      y: Math.max(this.image.pxStartY, Math.min(point.y, this.image.pxEndY)),
    };
  }

  clampPercentageRectToImageBounds(rect: PercentageRect): PercentageRect {
    return {
      pctStartX: Math.max(this.image.pctStartX, Math.min(this.image.pctEndX, rect.pctStartX)),
      pctStartY: Math.max(this.image.pctStartY, Math.min(this.image.pctEndY, rect.pctStartY)),
      pctEndX: Math.max(this.image.pctStartX, Math.min(this.image.pctEndX, rect.pctEndX)),
      pctEndY: Math.max(this.image.pctStartY, Math.min(this.image.pctEndY, rect.pctEndY)),
    };
  }

  normalizePercentageRect(rect: PercentageRect): PercentageRect {
    const normalizedRect = { ...rect };

    if (rect.pctStartX > rect.pctEndX) {
      normalizedRect.pctStartX = rect.pctEndX;
      normalizedRect.pctEndX = rect.pctStartX;
    }

    if (rect.pctStartY > rect.pctEndY) {
      normalizedRect.pctStartY = rect.pctEndY;
      normalizedRect.pctEndY = rect.pctStartY;
    }

    return normalizedRect;
  }

  center([boundingBox, imageBox]: [PercentageRect, PercentageRect]): [PercentageRect, PercentageRect] {
    // Calculate current box dimensions
    const boxWidth = boundingBox.pctEndX - boundingBox.pctStartX;
    const boxHeight = boundingBox.pctEndY - boundingBox.pctStartY;

    // Calculate centered position (0.5 is center of container)
    const newBoxStartX = (1 - boxWidth) / 2;
    const newBoxStartY = (1 - boxHeight) / 2;

    // Calculate the shift needed to center the box
    const shiftX = newBoxStartX - boundingBox.pctStartX;
    const shiftY = newBoxStartY - boundingBox.pctStartY;

    // Apply the same shift to the image to maintain relative positioning
    const newImageStartX = imageBox.pctStartX + shiftX;
    const newImageStartY = imageBox.pctStartY + shiftY;

    // Calculate image dimensions
    const imageWidth = imageBox.pctEndX - imageBox.pctStartX;
    const imageHeight = imageBox.pctEndY - imageBox.pctStartY;

    return [
      {
        pctStartX: newBoxStartX,
        pctStartY: newBoxStartY,
        pctEndX: newBoxStartX + boxWidth,
        pctEndY: newBoxStartY + boxHeight,
      },
      {
        pctStartX: newImageStartX,
        pctStartY: newImageStartY,
        pctEndX: newImageStartX + imageWidth,
        pctEndY: newImageStartY + imageHeight,
      },
    ];
  }

  tooCloseToEdge([boundingBox]: [PercentageRect, PercentageRect], margin: number): boolean {
    // Check if bounding box is within 10% of any edge
    const nearLeftEdge = boundingBox.pctStartX < margin;
    const nearTopEdge = boundingBox.pctStartY < margin;
    const nearRightEdge = boundingBox.pctEndX > 1 - margin;
    const nearBottomEdge = boundingBox.pctEndY > 1 - margin;

    return nearLeftEdge || nearTopEdge || nearRightEdge || nearBottomEdge;
  }

  hasRoomToGrow([boundingBox]: [PercentageRect, PercentageRect], pctMargin: number) {
    const farFromLeftEdge = boundingBox.pctStartX > pctMargin;
    const farFromTopEdge = boundingBox.pctStartY > pctMargin;
    const farFromRightEdge = boundingBox.pctEndX < 1 - pctMargin;
    const farFromBottomEdge = boundingBox.pctEndY < 1 - pctMargin;

    return farFromLeftEdge && farFromTopEdge && farFromRightEdge && farFromBottomEdge;
  }

  zoomIn([boundingBox, imageBox]: [PercentageRect, PercentageRect], factor: number): [PercentageRect, PercentageRect] {
    // Enlarge factor (inverse of shrink)
    const enlargeFactor = 1 / factor;
    const zoomFactor = enlargeFactor;

    // Calculate current box dimensions
    const boxWidth = boundingBox.pctEndX - boundingBox.pctStartX;
    const boxHeight = boundingBox.pctEndY - boundingBox.pctStartY;

    // Calculate new box dimensions (enlarge from top-left corner)
    const newBoxWidth = boxWidth * enlargeFactor;
    const newBoxHeight = boxHeight * enlargeFactor;

    // Keep the same top-left corner position for the box
    const newBoxStartX = boundingBox.pctStartX;
    const newBoxStartY = boundingBox.pctStartY;

    // Calculate image's relative position to the bounding box
    const relativeImageX = imageBox.pctStartX - boundingBox.pctStartX;
    const relativeImageY = imageBox.pctStartY - boundingBox.pctStartY;

    // Calculate image dimensions
    const imageWidth = imageBox.pctEndX - imageBox.pctStartX;
    const imageHeight = imageBox.pctEndY - imageBox.pctStartY;

    // Calculate new image dimensions (zoomed in)
    const newImageWidth = imageWidth * zoomFactor;
    const newImageHeight = imageHeight * zoomFactor;

    // Position the image at the same relative position to the new (enlarged) box
    const newImageStartX = newBoxStartX + relativeImageX * enlargeFactor;
    const newImageStartY = newBoxStartY + relativeImageY * enlargeFactor;

    return [
      {
        pctStartX: newBoxStartX,
        pctStartY: newBoxStartY,
        pctEndX: newBoxStartX + newBoxWidth,
        pctEndY: newBoxStartY + newBoxHeight,
      },
      {
        pctStartX: newImageStartX,
        pctStartY: newImageStartY,
        pctEndX: newImageStartX + newImageWidth,
        pctEndY: newImageStartY + newImageHeight,
      },
    ];
  }

  zoomOut([boundingBox, imageBox]: [PercentageRect, PercentageRect], factor: number): [PercentageRect, PercentageRect] {
    // Shrink factor
    const shrinkFactor = factor;
    const zoomFactor = shrinkFactor;

    // Calculate current box dimensions
    const boxWidth = boundingBox.pctEndX - boundingBox.pctStartX;
    const boxHeight = boundingBox.pctEndY - boundingBox.pctStartY;

    // Calculate new box dimensions (shrink from top-left corner)
    const newBoxWidth = boxWidth * shrinkFactor;
    const newBoxHeight = boxHeight * shrinkFactor;

    // Keep the same top-left corner position for the box
    const newBoxStartX = boundingBox.pctStartX;
    const newBoxStartY = boundingBox.pctStartY;

    // Calculate image's relative position to the bounding box
    const relativeImageX = imageBox.pctStartX - boundingBox.pctStartX;
    const relativeImageY = imageBox.pctStartY - boundingBox.pctStartY;

    // Calculate image dimensions
    const imageWidth = imageBox.pctEndX - imageBox.pctStartX;
    const imageHeight = imageBox.pctEndY - imageBox.pctStartY;

    // Calculate new image dimensions (zoomed out)
    const newImageWidth = imageWidth * zoomFactor;
    const newImageHeight = imageHeight * zoomFactor;

    // Position the image at the same relative position to the new (shrunken) box
    const newImageStartX = newBoxStartX + relativeImageX * shrinkFactor;
    const newImageStartY = newBoxStartY + relativeImageY * shrinkFactor;

    return [
      {
        pctStartX: newBoxStartX,
        pctStartY: newBoxStartY,
        pctEndX: newBoxStartX + newBoxWidth,
        pctEndY: newBoxStartY + newBoxHeight,
      },
      {
        pctStartX: newImageStartX,
        pctStartY: newImageStartY,
        pctEndX: newImageStartX + newImageWidth,
        pctEndY: newImageStartY + newImageHeight,
      },
    ];
  }

  setImagePctPosition({ pctStartX, pctStartY }: PercentageRect) {
    const point = this.toPixelPoint({ pctX: pctStartX, pctY: pctStartY });
    this.setImagePosition({
      x: point.x - this.initialImageX,
      y: point.y - this.initialImageY,
    });
  }
}
